import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  RefreshCw,
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Pause,
  Play,
  Zap,
  Pencil,
  Trash2,
  CircleSlash,
} from 'lucide-react';

interface RecurringSchedule {
  id: string;
  accountName: string;
  businessProfileName: string;
  contractTitle: string | null;
  applicationName: string | null;
  name: string;
  frequency: string;
  billingDayOfMonth: number;
  nextBillingDate: string;
  status: string;
  subtotal: number;
  enablePaymentReminders: boolean;
  autoGenerate: boolean;
  generatedInvoiceCount: number;
}

const statusVariant: Record<string, 'default' | 'success' | 'warning' | 'destructive' | 'secondary'> = {
  active: 'success',
  paused: 'warning',
  cancelled: 'secondary',
  completed: 'secondary',
};

const frequencyLabel: Record<string, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semi_annual: 'Semi-Annual',
  annual: 'Annual',
};

function billingDayLabel(day: number): string {
  if (day >= 31) return 'last day';
  const suffix = day % 10 === 1 && day !== 11 ? 'st' : day % 10 === 2 && day !== 12 ? 'nd' : day % 10 === 3 && day !== 13 ? 'rd' : 'th';
  return `${day}${suffix}`;
}

function isDue(nextBillingDate: string): boolean {
  if (!nextBillingDate) return false;
  const today = new Date().toISOString().split('T')[0];
  return nextBillingDate.split('T')[0] <= today;
}

export function RecurringSchedulesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['recurringSchedules', { page, search, status: statusFilter }],
    queryFn: async () => {
      return api.getRecurringSchedules({
        page,
        limit: 20,
        search: search || undefined,
        status: statusFilter || undefined,
      });
    },
  });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['recurringSchedules'] });
    queryClient.invalidateQueries({ queryKey: ['dueSchedules'] });
    queryClient.invalidateQueries({ queryKey: ['invoices'] });
  }

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteRecurringSchedule(id),
    onSuccess: () => invalidate(),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.setRecurringScheduleStatus(id, status),
    onMutate: ({ id }) => setBusyId(id),
    onSettled: () => setBusyId(null),
    onSuccess: (_result, { status }) => {
      invalidate();
      setNotice({
        kind: 'success',
        message: status === 'paused' ? 'Schedule paused — no invoices will be created until you resume it.' : 'Schedule resumed.',
      });
    },
  });

  const generateMutation = useMutation({
    mutationFn: async (id: string) => {
      const result = await api.generateInvoice(id);
      if (!result.success) {
        throw new Error(result.error?.message || 'Could not generate an invoice for this schedule.');
      }
      return result;
    },
    onMutate: (id) => setBusyId(id),
    onSettled: () => setBusyId(null),
    onSuccess: (result) => {
      invalidate();
      const invoiceId = result?.data?.invoiceId;
      setNotice({
        kind: 'success',
        message: invoiceId ? 'Draft invoice created.' : 'Invoice generated.',
      });
      if (invoiceId) navigate(`/billing/invoices/${invoiceId}`);
    },
    onError: (err: unknown) => {
      setNotice({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Could not generate an invoice.',
      });
    },
  });

  const schedules = (data?.data || []) as RecurringSchedule[];
  const pagination = data?.pagination;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Recurring Billing</h1>
          <p className="text-muted-foreground">
            Billing templates that create invoices on a schedule. To make an existing invoice
            recurring, open it and choose <strong>Make Recurring</strong>.
          </p>
        </div>
        <Button onClick={() => navigate('new')}>
          <Plus className="h-4 w-4 mr-2" />
          New Schedule
        </Button>
      </div>

      {notice && (
        <div
          className={`flex items-center justify-between gap-3 rounded-lg border px-4 py-2 text-sm ${
            notice.kind === 'error'
              ? 'border-destructive/40 bg-destructive/10 text-destructive'
              : 'bg-muted/50'
          }`}
        >
          <span>{notice.message}</span>
          <Button variant="ghost" size="sm" onClick={() => setNotice(null)}>
            Dismiss
          </Button>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search schedules..."
            className="pl-9"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : schedules.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <RefreshCw className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No recurring schedules found</p>
            <p className="text-sm text-muted-foreground mt-1 max-w-md text-center">
              Open any invoice and click <strong>Make Recurring</strong> to turn it into a schedule,
              or build one from scratch with New Schedule.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-3 text-sm font-medium">Name</th>
                  <th className="text-left p-3 text-sm font-medium">Account</th>
                  <th className="text-left p-3 text-sm font-medium">Frequency</th>
                  <th className="text-left p-3 text-sm font-medium">Next Billing</th>
                  <th className="text-right p-3 text-sm font-medium">Amount</th>
                  <th className="text-left p-3 text-sm font-medium">Status</th>
                  <th className="text-right p-3 text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {schedules.map((s) => {
                  const busy = busyId === s.id;
                  const due = s.status === 'active' && isDue(s.nextBillingDate);
                  return (
                    <tr key={s.id} className="border-b hover:bg-muted/30 cursor-pointer" onClick={() => navigate(s.id)}>
                      <td className="p-3 text-sm">
                        <div className="font-medium">{s.name}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-2">
                          {s.contractTitle && <span>Contract: {s.contractTitle}</span>}
                          {s.generatedInvoiceCount > 0 && (
                            <span>
                              {s.generatedInvoiceCount} invoice{s.generatedInvoiceCount === 1 ? '' : 's'}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-sm">{s.accountName}</td>
                      <td className="p-3 text-sm">
                        {frequencyLabel[s.frequency] || s.frequency}
                        <span className="text-muted-foreground ml-1">
                          on the {billingDayLabel(s.billingDayOfMonth)}
                        </span>
                      </td>
                      <td className="p-3 text-sm">
                        <div className="flex items-center gap-2">
                          {formatDate(s.nextBillingDate)}
                          {due && (
                            <Badge variant="warning" className="text-[10px]">Due now</Badge>
                          )}
                        </div>
                        {s.status === 'active' && (
                          <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                            {s.autoGenerate ? (
                              <>
                                <Zap className="h-3 w-3" />
                                Generates automatically
                              </>
                            ) : (
                              <>
                                <CircleSlash className="h-3 w-3" />
                                Manual generation
                              </>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="p-3 text-sm text-right font-medium">{formatCurrency(s.subtotal)}</td>
                      <td className="p-3">
                        <Badge variant={statusVariant[s.status] || 'secondary'}>{s.status}</Badge>
                      </td>
                      <td className="p-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        {s.status === 'active' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Create this schedule's next invoice now"
                              disabled={busy}
                              onClick={() => generateMutation.mutate(s.id)}
                            >
                              {busy && generateMutation.isPending
                                ? <Loader2 className="h-4 w-4 animate-spin" />
                                : <Zap className="h-4 w-4" />}
                              <span className="ml-1 hidden xl:inline">Generate</span>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Pause billing — no invoices until resumed"
                              disabled={busy}
                              onClick={() => statusMutation.mutate({ id: s.id, status: 'paused' })}
                            >
                              <Pause className="h-4 w-4" />
                              <span className="ml-1 hidden xl:inline">Pause</span>
                            </Button>
                          </>
                        )}
                        {s.status === 'paused' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Resume billing on this schedule"
                            disabled={busy}
                            onClick={() => statusMutation.mutate({ id: s.id, status: 'active' })}
                          >
                            <Play className="h-4 w-4" />
                            <span className="ml-1 hidden xl:inline">Resume</span>
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => navigate(s.id)}>
                          <Pencil className="h-4 w-4" />
                          <span className="ml-1 hidden xl:inline">Edit</span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          title={
                            s.generatedInvoiceCount > 0
                              ? 'This schedule has generated invoices — it will be cancelled rather than deleted'
                              : 'Delete this schedule'
                          }
                          onClick={() => {
                            const message = s.generatedInvoiceCount > 0
                              ? `"${s.name}" has already generated ${s.generatedInvoiceCount} invoice(s), so it will be cancelled instead of deleted. Those invoices are kept. Continue?`
                              : `Delete "${s.name}"? This cannot be undone.`;
                            if (confirm(message)) deleteMutation.mutate(s.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {pagination.total} schedule{pagination.total !== 1 ? 's' : ''}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm">Page {page} of {pagination.totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= pagination.totalPages} onClick={() => setPage(p => p + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
